import { Button } from "@/components/ui/button";
import { ChevronLeft, CheckCircle, Calendar, Tag, List, Package, Info, Loader2, AlertTriangle, Pencil } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Pro017List } from "../create-pro017-form";
import { formatDateToBR } from "../../../utils/date-utils";

export interface Pro017CampaignData {
  description: string;
  startDate: string;
  endDate: string;
  discountValue: number;
  discountType: number;
  lists: Pro017List[];
}

interface CampaignConflictItem {
  itemCode: string;
  itemName: string;
  conflictingCampaigns: Array<{
    code: number;
    description: string;
    type: string;
    startDate: string;
    endDate: string;
    storeCode: number;
  }>;
}

interface StepFiveProps {
  data: Pro017CampaignData;
  prevStep: () => void;
  onSubmit: () => void;
  isLoading: boolean;
  mode?: 'create' | 'edit';
  conflicts?: CampaignConflictItem[];
  onEditProducts?: () => void;
  originalSkusPerList?: Record<number, Set<string>>;
  validationProgress?: { current: number; total: number } | null;
}

export default function StepFive({ data, prevStep, onSubmit, isLoading, mode = 'create', conflicts = [], onEditProducts, originalSkusPerList, validationProgress }: StepFiveProps) {
  const isPendingSku = (listNumber: number, sku: string): boolean => {
    if (mode !== "edit" || !originalSkusPerList) return false;
    const original = originalSkusPerList[listNumber];
    if (!original) return false;
    const normalized = String(sku).trim().toUpperCase();
    return !original.has(normalized);
  };

  const totalPending = data.lists.reduce(
    (sum, list) =>
      sum + list.items.filter((sku) => isPendingSku(list.listNumber, sku)).length,
    0,
  );
  const getDiscountLabel = () => {
    if (data.discountType === 0) {
      return `R$ ${data.discountValue.toFixed(2)}`;
    } else {
      return `${data.discountValue}%`;
    }
  };

  const totalItems = data.lists.reduce((sum, list) => sum + list.items.length, 0);
  const totalCombinations = data.lists.reduce((product, list) => product * list.items.length, 1);

  return (
    <div className="space-y-4 animate-in fade-in duration-300">
      <div className="bg-card rounded-lg border p-4 space-y-4">
        <div className="flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-primary" />
          <div>
            <h3 className="text-base font-semibold">
              Revisar e Confirmar Campanha
            </h3>
            <p className="text-sm text-muted-foreground">
              Confira todos os dados antes de {mode === 'create' ? 'criar' : 'atualizar'} a campanha
            </p>
          </div>
        </div>

        {mode === "edit" && totalPending > 0 && (
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-amber-900">
              <p className="font-semibold mb-0.5">
                {totalPending} item(ns) ainda não salvos
              </p>
              <p>
                Os itens marcados como <strong>novos</strong> abaixo serão adicionados ao banco ao clicar em <strong>Salvar Alterações</strong>. Se sair sem salvar, serão descartados.
              </p>
            </div>
          </div>
        )}

        {validationProgress && (
          <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-lg p-3 flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
            <p className="text-xs text-blue-900 dark:text-blue-300">
              Validando conflitos: lote {validationProgress.current} de {validationProgress.total}
            </p>
          </div>
        )}

        {/* Basic Information */}
        <div className="bg-secondary/20 rounded-lg border p-4 space-y-3">
          <h4 className="text-sm font-semibold flex items-center gap-2">
            <Info className="h-4 w-4 text-primary" />
            Informações Básicas
          </h4>
          <div className="grid gap-3">
            <div>
              <p className="text-xs text-muted-foreground">Descrição</p>
              <p className="text-sm font-medium">
                {data.description}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  Data de Início
                </p>
                <p className="text-sm font-medium">
                  {formatDateToBR(data.startDate)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  Data de Fim
                </p>
                <p className="text-sm font-medium">
                  {formatDateToBR(data.endDate)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Discount Configuration */}
        <div className="bg-secondary/20 rounded-lg border p-4 space-y-3">
          <h4 className="text-sm font-semibold flex items-center gap-2">
            <Tag className="h-4 w-4 text-primary" />
            Configuração de Desconto
          </h4>
          <div className="grid gap-3">
            <div>
              <p className="text-xs text-muted-foreground">Tipo de Desconto</p>
              <p className="text-sm font-medium">
                {data.discountType === 0 ? "R$ - Valor Fixo" : "% - Percentual"}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Valor/Percentual</p>
              <p className="text-lg font-bold text-primary">{getDiscountLabel()}</p>
            </div>
          </div>
        </div>

        {/* Lists Summary */}
        <div className="bg-secondary/20 rounded-lg border p-4 space-y-3">
          <h4 className="text-sm font-semibold flex items-center gap-2">
            <List className="h-4 w-4 text-primary" />
            Resumo das Listas
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <p className="text-xs text-muted-foreground">Total de Listas</p>
              <p className="text-lg font-bold text-primary">{data.lists.length}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total de Produtos</p>
              <p className="text-lg font-bold text-primary">{totalItems}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Produtos/Lista (Média)</p>
              <p className="text-lg font-bold text-primary">
                {(totalItems / data.lists.length).toFixed(1)}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Combinações</p>
              <p className="text-lg font-bold text-primary">{totalCombinations}</p>
            </div>
          </div>
        </div>

        {/* Lists Detail */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold flex items-center gap-2">
            <Package className="h-4 w-4 text-primary" />
            Detalhes das Listas
          </h4>
          <div className="grid gap-3">
            {data.lists.map((list) => (
              <div
                key={list.listNumber}
                className="rounded-lg border bg-card p-3 space-y-2"
              >
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-sm font-bold text-primary">{list.listNumber}</span>
                    </div>
                    <p className="text-sm font-semibold">
                      Lista {list.listNumber}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {(() => {
                      const pendingInList = list.items.filter((sku) =>
                        isPendingSku(list.listNumber, sku),
                      ).length;
                      return pendingInList > 0 ? (
                        <Badge
                          variant="outline"
                          className="border-amber-400 bg-amber-100 text-amber-800 text-xs"
                        >
                          +{pendingInList} novo(s)
                        </Badge>
                      ) : null;
                    })()}
                    <Badge variant="default">{list.items.length} produto(s)</Badge>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 ml-10">
                  {list.items.map((itemCode, index) => {
                    const pending = isPendingSku(list.listNumber, itemCode);
                    return (
                      <Badge
                        key={`${itemCode}-${index}`}
                        variant="outline"
                        className={
                          pending
                            ? "text-xs border-amber-400 bg-amber-100 text-amber-800"
                            : "text-xs"
                        }
                      >
                        {itemCode}
                        {pending && <span className="ml-1 opacity-70">•novo</span>}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Conflitos de Produtos */}
        {conflicts.length > 0 && (
          <div className="border border-destructive/50 rounded-lg p-3 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-destructive" />
                <h3 className="text-sm font-semibold text-destructive">
                  {conflicts.length} produto(s) em conflito
                </h3>
              </div>
              {onEditProducts && (
                <Button variant="outline" size="sm" onClick={onEditProducts}>
                  <Pencil className="mr-1.5 h-3.5 w-3.5" />
                  Editar Produtos
                </Button>
              )}
            </div>
            <div className="max-h-48 overflow-y-auto space-y-2">
              {conflicts.slice(0, 20).map((conflict) => (
                <div
                  key={conflict.itemCode}
                  className="border rounded p-2 space-y-1"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-medium">{conflict.itemCode}</span>
                    {conflict.itemName && (
                      <span className="text-xs text-muted-foreground">{conflict.itemName}</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {conflict.conflictingCampaigns.map((campaign, idx) => (
                      <Badge key={`${campaign.code}-${idx}`} variant="destructive" className="text-xs">
                        {campaign.type} #{campaign.code}
                        {campaign.storeCode !== 999 && ` (Loja ${campaign.storeCode})`}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
              {conflicts.length > 20 && (
                <p className="text-xs text-muted-foreground text-center py-1">
                  +{conflicts.length - 20} produto(s) com conflito
                </p>
              )}
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 text-blue-600 dark:text-blue-500 mt-0.5" />
            <div className="text-xs text-blue-900 dark:text-blue-300">
              <p className="font-semibold mb-1">Como funciona o combo:</p>
              <p>
                O cliente precisa comprar <strong>1 produto de cada lista</strong> para receber o desconto de{" "}
                <strong>{getDiscountLabel()}</strong>. Com {data.lists.length} listas e um total de{" "}
                {totalItems} produtos, existem <strong>{totalCombinations} combinações possíveis</strong> de combos.
              </p>
            </div>
          </div>
        </div>

        {/* Warning Box */}
        <div className="bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-900 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 text-yellow-600 dark:text-yellow-500 mt-0.5" />
            <div className="text-xs text-yellow-900 dark:text-yellow-300">
              <p className="font-semibold mb-1">Atenção:</p>
              <p>
                Após criar a campanha, você poderá adicionar ou remover produtos das listas, mas não poderá
                alterar a quantidade de listas nem os dados básicos da campanha.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between pt-2">
        <Button
          variant="outline"
          onClick={prevStep}
          disabled={isLoading}
          className="w-full md:w-auto"
        >
          <ChevronLeft className="mr-2 h-4 w-4" />
          Voltar
        </Button>
        <Button
          onClick={onSubmit}
          disabled={isLoading || conflicts.length > 0}
          className="w-full md:w-auto"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {mode === 'create' ? 'Criando Campanha...' : 'Salvando Alterações...'}
            </>
          ) : (
            <>
              <CheckCircle className="mr-2 h-4 w-4" />
              {mode === 'create' ? 'Criar Campanha' : 'Salvar Alterações'}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
